/**
 * React Query hooks for SharePoint API
 */

import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { sharePointApi, ApiRequestError } from '../services/api';
import type { SyncItem, FolderContents } from '../services/api';

// =============================================================================
// Query Keys
// =============================================================================

export const sharePointKeys = {
  all: ['sharepoint'] as const,
  folders: () => [...sharePointKeys.all, 'folders'] as const,
  folder: (path: string, driveId?: string, filterExcel?: boolean) =>
    [...sharePointKeys.folders(), path, driveId, filterExcel] as const,
  folderScan: (jobId: string) => [...sharePointKeys.all, 'folder-scan', jobId] as const,
};

// =============================================================================
// Queries
// =============================================================================

/**
 * Hook to browse SharePoint folders with infinite scroll.
 *
 * Returns the first page of items (50) immediately, then loads more
 * pages as `fetchNextPage()` is called. All pages are merged into
 * a single `data` object with the same FolderContents shape.
 *
 * `data.total_items` contains the folder's total child count (from first page).
 */
export function useBrowseSharePoint(
  path: string = '/',
  driveId?: string,
  filterExcel: boolean = false
) {
  const infiniteQuery = useInfiniteQuery({
    queryKey: sharePointKeys.folder(path, driveId, filterExcel),
    queryFn: ({ pageParam }) =>
      sharePointApi.getFolders(path, driveId, filterExcel, false, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_page_token ?? undefined,
    enabled: !!path,
    staleTime: 60000,
    retry: 2,
    retryDelay: (_attempt, error) => {
      if (error instanceof ApiRequestError && error.retryAfter) {
        return error.retryAfter * 1000;
      }
      return Math.min(1000 * 2 ** _attempt, 30000);
    },
  });

  // Merge all pages into a single FolderContents for backwards compatibility
  const data = useMemo<(FolderContents & { total_items?: number }) | undefined>(() => {
    if (!infiniteQuery.data?.pages?.length) return undefined;

    const firstPage = infiniteQuery.data.pages[0];
    const allFolders = infiniteQuery.data.pages.flatMap((p) => p.folders);
    const allFiles = infiniteQuery.data.pages.flatMap((p) => p.files);
    const lastPage = infiniteQuery.data.pages[infiniteQuery.data.pages.length - 1];

    // Recompute stats from all accumulated pages
    const totalSize = allFiles.reduce((sum, f) => sum + (f.size || 0), 0);
    const excelCount = allFiles.filter(
      (f) => f.name?.toLowerCase().endsWith('.xlsx') || f.name?.toLowerCase().endsWith('.xls')
    ).length;

    return {
      folders: allFolders,
      files: allFiles,
      current_path: firstPage.current_path,
      parent_path: firstPage.parent_path,
      breadcrumbs: firstPage.breadcrumbs,
      stats: {
        folder_count: allFolders.length,
        file_count: allFiles.length,
        total_size: totalSize,
        excel_count: excelCount,
      },
      drive_id: firstPage.drive_id,
      site_id: firstPage.site_id,
      has_more: lastPage?.has_more,
      total_items: firstPage.total_items,
    };
  }, [infiniteQuery.data]);

  return {
    data,
    isLoading: infiniteQuery.isLoading,
    error: infiniteQuery.error,
    refetch: infiniteQuery.refetch,
    hasNextPage: infiniteQuery.hasNextPage,
    fetchNextPage: infiniteQuery.fetchNextPage,
    isFetchingNextPage: infiniteQuery.isFetchingNextPage,
  };
}

/**
 * Hook to prefetch the first page of a SharePoint folder.
 */
export function usePrefetchFolder(
  path: string,
  driveId?: string,
  filterExcel: boolean = false
) {
  const queryClient = useQueryClient();
  return useCallback(() => {
    queryClient.prefetchInfiniteQuery({
      queryKey: sharePointKeys.folder(path, driveId, filterExcel),
      queryFn: () => sharePointApi.getFolders(path, driveId, filterExcel),
      initialPageParam: undefined as string | undefined,
      staleTime: 60000,
    });
  }, [path, driveId, filterExcel, queryClient]);
}

/**
 * Hook to fetch ALL Excel files from a SharePoint folder via async worker.
 *
 * Works for both recursive (subfolders) and non-recursive (single large folder).
 * Uses the async job pattern to bypass the 29s API Gateway timeout.
 */
export function useSelectAllFiles(
  path: string = '/',
  driveId?: string,
  recursive: boolean = false,
) {
  const [jobId, setJobId] = useState<string | null>(null);
  const [scanResult, setScanResult] = useState<FolderContents | null>(null);

  const startScan = useQuery({
    queryKey: [...sharePointKeys.folder(path, driveId, true), 'select-all', recursive] as const,
    queryFn: async () => {
      setJobId(null);
      setScanResult(null);
      const result = await sharePointApi.startFolderScan(path, driveId, recursive);
      if (result.job_id) {
        // Async path (recursive): poll for results
        setJobId(result.job_id);
      } else if (result.files) {
        // Synchronous path (non-recursive): files returned directly
        setScanResult({
          folders: result.folders ?? [],
          files: result.files,
          current_path: result.current_path ?? path,
          parent_path: result.parent_path ?? '',
          breadcrumbs: result.breadcrumbs,
          stats: result.stats,
        });
      }
      return result;
    },
    enabled: false,
  });

  const pollStatus = useQuery({
    queryKey: sharePointKeys.folderScan(jobId ?? ''),
    queryFn: () => sharePointApi.getFolderScanStatus(jobId!),
    enabled: !!jobId && !scanResult,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'processing') return 2000;
      return false;
    },
  });

  // Extract folder contents when job completes
  useEffect(() => {
    if (pollStatus.data?.status === 'completed' && !scanResult && pollStatus.data.files) {
      const completed = pollStatus.data;
      setScanResult({
        folders: completed.folders ?? [],
        files: completed.files ?? [],
        current_path: completed.current_path ?? path,
        parent_path: completed.parent_path ?? '',
        breadcrumbs: completed.breadcrumbs,
        stats: completed.stats,
      });
    }
  }, [pollStatus.data, scanResult, path]);

  return {
    data: scanResult,
    isLoading: startScan.isFetching || (!!jobId && !scanResult && pollStatus.data?.status !== 'failed'),
    error: startScan.error || pollStatus.error || (pollStatus.data?.status === 'failed' ? new Error(pollStatus.data.error_message ?? 'Scan failed') : null),
    progress: pollStatus.data ? {
      status: pollStatus.data.status,
      foldersScanned: pollStatus.data.folders_scanned,
      filesFound: pollStatus.data.files_found,
    } : null,
    refetch: startScan.refetch,
  };
}


// =============================================================================
// Mutations
// =============================================================================

export function useSyncSharePointItems() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      items,
      options,
    }: {
      items: SyncItem[];
      options?: { recursive?: boolean };
    }) => sharePointApi.syncItems(items, options),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sharePointKeys.folders() });
    },
  });
}
